/* eslint-disable no-console */
/* eslint-disable react/no-array-index-key */
import React, { FunctionComponent, useMemo, useRef, useState } from "react";
import { styled, StyledIconButton } from "@iventis/styles";
import { StyledInteractiveArea } from "@iventis/styles/src/components";
import { Content } from "@iventis/translations";
import { Checkbox, useTheme } from "@mui/material";
import { useTreeBrowser } from "@iventis/tree-browser";
import { Header6 } from "@iventis/styles/src/components/texts";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { CursorTooltip, DisabledOverlay } from "@iventis/components";
import { LibraryOverflowPopover } from "@iventis/tree-browser/implementations/library/components/library-overflow-popover";
import { StyledEllipsis } from "@iventis/tree-browser/implementations/library/styles/library-node.styles";
import { NewStyledCheckBoxContainer } from "@iventis/tree-browser/implementations/library/styles/library-shared-node.styles";
import { LibraryNodeProps } from "@iventis/tree-browser/implementations/library/types/library-node.types";
import { TreeBrowserDomainNodeContext } from "@iventis/tree-browser/implementations/library/helpers/library.helpers";
import { dataTestIds } from "@iventis/testing";
import { Theme } from "@emotion/react";
import { StyledLibraryTileNode } from "./library-tile-node.styles";
import { FavouriteButtonComponent } from "./library-nodes";

export const LibraryTileNodeComponent: FunctionComponent<LibraryNodeProps> = ({
    content,
    node,
    open,
    text,
    overflowOptions,
    setFavourite,
    preview,
    disableActions,
    draggingNode,
    disabled = false,
    isDragging = false,
    subtitle,
    selectable = { disabled: false },
}) => {
    const theme = useTheme<Theme>();

    const [{ selectedNodeIds }, treeBrowserContext] = useTreeBrowser(TreeBrowserDomainNodeContext, node.id, "selectedNodeIds");

    const selected = selectedNodeIds.includes(node.id);
    const overflowButtonRef = useRef<HTMLDivElement>();
    const [overflowOpen, setOverflowOpen] = useState<boolean>(false);

    const nameContainerRef = useRef<HTMLDivElement>();

    const textInOverflow = useMemo(() => nameContainerRef.current?.offsetHeight < nameContainerRef.current?.scrollHeight, [node, nameContainerRef.current]);

    const translate = useIventisTranslate();

    return (
        <>
            <CursorTooltip text={translate(Content.library2.permissions.no_read_permissions)} disabled={!disabled}>
                <StyledLibraryTileNode
                    singleTopRowButton={disableActions}
                    fontWeight={400}
                    borderColour={theme.shades.three}
                    selected={selected}
                    data-testid={dataTestIds.library.tileNode(node.name)}
                    emptyNode={false}
                    onClick={(event) => {
                        if (!disableActions) {
                            treeBrowserContext.toggleSelectNodes([node], undefined);
                        }
                        // Stops the event going further in the DOM which made the node to be toggled twice
                        event.preventDefault();
                    }}
                    onDoubleClick={!disabled && open}
                    isFavourite={node.favourite}
                    aria-selected={selected}
                    draggingNode={draggingNode}
                >
                    {disabled && <DisabledOverlay />}

                    <div className="gallery-card-top-row">
                        {!draggingNode && (
                            <>
                                {!disableActions && !selectable.disabled && (
                                    <>
                                        <NewStyledCheckBoxContainer onDoubleClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                className="colour-on-focus-light"
                                                size="small"
                                                checked={selected}
                                                onChange={() => treeBrowserContext.toggleSelectNodes([node], undefined)}
                                                color="default"
                                                disabled={selectable.disabled}
                                                data-testid={`select-node-${node.name}-checkbox`}
                                            />
                                        </NewStyledCheckBoxContainer>
                                    </>
                                )}

                                {typeof setFavourite === "function" && !disabled && (
                                    <FavouriteButtonComponent checked={node.favourite} onChange={setFavourite} className="favourite" />
                                )}

                                {!disableActions && overflowOptions?.length > 0 && (
                                    <div className="gallery-ellipsis" ref={overflowButtonRef}>
                                        <StyledIconButton
                                            data-testid={`${node.name}-overflow-button`}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                setOverflowOpen(true);
                                            }}
                                        >
                                            <span className="hidden">{text.screenReaderOverflowMessage}</span>
                                            <StyledEllipsis className="colour-on-focus" icon="ellipsis" size="xs" />
                                        </StyledIconButton>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="gallery-card-main">
                        <div className="gallery-thumbnail">
                            {preview}
                            <StyledInteractiveArea
                                onClick={(event) => {
                                    event.stopPropagation();
                                    open();
                                }}
                            />
                        </div>
                        <div className="content">
                            <StyledHeader ref={nameContainerRef}>{node.name}</StyledHeader>
                            <CursorTooltip text={node.name} disabled={isDragging || !textInOverflow}>
                                <StyledInteractiveArea
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        open();
                                    }}
                                    data-cy={`open-node-${node.id}`}
                                    data-testid={`open-node-${node.name}`}
                                />
                            </CursorTooltip>
                            {subtitle != null && <div className="breadcrumbs-container">{subtitle}</div>}
                            {content}
                        </div>
                    </div>

                    {overflowOptions?.length ? (
                        <LibraryOverflowPopover
                            overflowButtonRef={overflowButtonRef}
                            overflowOpen={overflowOpen}
                            overflowOptions={overflowOptions}
                            setOverflowOpen={setOverflowOpen}
                            node={node}
                        />
                    ) : (
                        <></>
                    )}
                </StyledLibraryTileNode>
            </CursorTooltip>
        </>
    );
};

export const StyledHeader = styled(Header6)`
    overflow: hidden;
    height: 3em; // Allow for space for two lines even if 2 lines aren't being shown
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
    word-break: break-word;
    hyphens: auto;
`;
